/**
 * PasswordInput — shadcn-style password field with an inline visibility toggle.
 *
 * Wraps the base <Input> primitive and adds a trailing button that flips the
 * underlying `<input>` between `type="password"` and `type="text"`. The toggle
 * button is rendered as a sibling of the input (NOT inside the form's <label>)
 * so click-to-focus on the label stays correct, and the input retains focus
 * after toggling.
 *
 * Accessibility:
 *   - The toggle is a `<button type="button">` so it never submits the parent
 *     form.
 *   - `aria-pressed` reflects the visible/hidden state; `aria-label` swaps
 *     between «Показать пароль» and «Скрыть пароль» (component-local literals;
 *     not in the domain locale because they're UI-primitive copy).
 *   - `tabIndex={-1}` keeps keyboard users on the natural password → submit
 *     tab path. The toggle is still operable by mouse, touch, and screen-
 *     reader virtual-cursor click.
 *
 * Touch-target:
 *   ⚠️ ПРЕЖНЯЯ ВРЕЗКА БЫЛА ЛОЖНОЙ ДВАЖДЫ, И СНЯТА ОНА ОТМЕНОЙ С НОВЫМ
 *   ОСНОВАНИЕМ, А НЕ СТЁРТА (форма Р-5). Она гласила: «`w-9` (36px) × высота
 *   родительского `Input` (h-8 = 32px на десктопе, h-10 = 40px, когда `Input`
 *   поднят на мобильных брейкпоинтах); 36 × 40 проходит WCAG 2.5.5 AA».
 *   ⚠️ Варианта `h-10` у `Input` НЕ БЫЛО НИКОГДА — ни класса, ни брейкпоинта,
 *   поднимающего высоту; то есть 40px, на которых держался вывод «проходит»,
 *   не существовали. Фактически было 36 × 32.
 *   ✅ Сегодня `Input` несёт `[@media(pointer:coarse)]:h-11` (Ф4 · В6-б), то
 *   есть на КАСАНИИ высота 44px, и площадь переключателя — 36 × 44. По меньшей
 *   стороне это 36 при норме 44: остаток компромисса СОХРАНЯЕТСЯ, но теперь он
 *   про ШИРИНУ, а не про высоту, и назван числом, а не выводом. Разбор —
 *   design-reference/tokens.md, "Hit-target overrides".
 */
import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input, type InputProps } from './input';

export type PasswordInputProps = Omit<InputProps, 'type'>;

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const [show, setShow] = React.useState(false);

    return (
      <div className="relative" data-disabled={disabled ? '' : undefined}>
        <Input
          {...props}
          ref={ref}
          disabled={disabled}
          type={show ? 'text' : 'password'}
          className={cn('pr-9', className)}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={show ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={show}
          onMouseDown={(e) => {
            // Prevent the browser from moving focus to the toggle on click;
            // the password input must keep focus so the user can continue
            // typing without an extra tab/click. Standard pattern for
            // "icon-in-input" affordances.
            e.preventDefault();
          }}
          onClick={() => setShow((v) => !v)}
          className={cn(
            'absolute inset-y-0 right-0 inline-flex w-9 items-center justify-center',
            'text-muted hover:text-ink transition-colors duration-short',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-wash',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          {show ? (
            <EyeOff className="size-4" aria-hidden="true" />
          ) : (
            <Eye className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
